import { defineStore } from 'pinia'
import { User } from '../apis/types/user'
import router from '../router'
import { validateAccessToken } from '../utils/utils'

import { useUserStore } from './useUserStore'
import { useUserJrs } from './useUserJrs'
import { Store } from '../apis/types/store'
import { Jrs } from '../apis/types/jrs'

export const useUser = defineStore('user', {
  state: (): User => {
    const cachedUserData = localStorage.getItem('user')
    if (!cachedUserData) return {} as User

    try {
      const user = JSON.parse(cachedUserData) as User

      if (user.userId && (user.accessToken || user.refreshToken)) {
        return user
      }
    } catch (e) {
      console.error('error: ', e)
    }

    return {} as User
  },

  actions: {
    toggle2FA() {
      this.is2FAEnabled = !this.is2FAEnabled
    },

    setUser(user: User) {
      Object.assign(this, user)
      localStorage.setItem('user', JSON.stringify(user))
    },

    setUserName(name: string) {
      this.name = name
    },

    getUser() {
      return this
    },

    setStores(stores: Store[]) {
      const userStore = useUserStore()
      userStore.setStores(stores)
    },

    getStores(): Store[] {
      const userStore = useUserStore()
      return userStore.stores
    },

    resetStores() {
      const userStore = useUserStore()
      userStore.resetStore()
    },

    setJrs(storeId: string, jrsList: Jrs[]) {
      if (jrsList.length > 3) {
        throw new Error('한 스토어에는 최대 3개의 JRS만 등록할 수 있습니다.')
      }
      const userJrs = useUserJrs()
      userJrs.setJrsByStoreId(storeId, jrsList)
    },

    getJrs(storeId: string): Jrs[] {
      const userJrs = useUserJrs()
      return userJrs.getJrs(storeId)
    },

    resetJrs() {
      const userJrs = useUserJrs()
      userJrs.resetJrs()
    },

    async isLoggedIn(): Promise<boolean> {
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') ?? '{}')
        const token = this.accessToken || storedUser.accessToken

        if (!token) return false

        if (storedUser.expiresIn && storedUser.expiresIn > 0) {
          if (Date.now() > storedUser.expiresIn) {
            return false
          }
        }

        if (!validateAccessToken(token)) {
          return false
        }

        return true
      } catch (e) {
        return false
      }
    },

    logout() {
      this.resetUser()
      localStorage.removeItem('user')

      const userStore = useUserStore()
      const userJrs = useUserJrs()
      userStore.resetStore()
      userJrs.resetJrs()

      router.push({ name: 'login' })
    },

    resetUser() {
      this.userId = ''
      this.name = ''
      this.profileImage = ''
      this.managerStores = []
      this.accessToken = ''
      this.refreshToken = ''
      this.expiresIn = 0
      this.keepLoggedIn = false
      this.is2FAEnabled = false
    },
  },
})
